import { motion } from 'framer-motion';
import { ArrowRight, Bot, Cpu, Wand2 } from 'lucide-react';

import {
  AGENT_TOOLS,
  EDITOR_BULLETS,
  LAYER_LABELS,
  NPC_CARDS,
  NPC_FEATURE_BULLETS,
} from './landingPageSections';

export function LandingPageNpcShowcase(): JSX.Element {
  return (
    <section
      className="border-y border-[#0ea5e9]/10 py-24"
      id="npcs"
      style={{ background: 'linear-gradient(180deg, #060f1e 0%, #050b1a 100%)' }}
    >
      <div className="mx-auto max-w-7xl px-5">
        <div className="grid grid-cols-1 items-center gap-16 lg:grid-cols-2">
          <div>
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#0ea5e9]/30 bg-[#0ea5e9]/12 px-3 py-1.5 text-xs font-bold tracking-wide text-[#7dd3fc] backdrop-blur-sm">
              <Bot className="h-3 w-3" />
              <span>AI-POWERED NPCS</span>
            </div>
            <h2 className="mb-6 text-4xl font-black leading-tight tracking-tight text-white">
              Characters with
              <br />
              <span className="text-[#38bdf8]">genuine memory</span>
              <br />
              and personality
            </h2>
            <p className="mb-8 text-lg leading-relaxed text-sky-300/80">
              Each of the 5 built-in NPCs has a distinct personality, a patrol route, and a
              long-term memory engine. They remember what you said last session. They form opinions.
              They grow.
            </p>
            <ul className="mb-8 space-y-3">
              {NPC_FEATURE_BULLETS.map((item) => (
                <li key={item.label} className="flex items-start gap-3 text-sm text-sky-300/80">
                  <div className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#22d3ee]" />
                  <span>{item.label}</span>
                </li>
              ))}
            </ul>
            <button
              className="group flex items-center gap-2 text-sm font-bold text-[#38bdf8] transition-colors hover:text-white"
              type="button"
            >
              <span>Meet the characters</span>
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {NPC_CARDS.map((npc, i) => (
              <motion.div
                key={npc.seed}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08 }}
                whileHover={{ y: -4, borderColor: 'rgba(34,211,238,0.45)' }}
                className={`cursor-pointer rounded-2xl border border-[#1e3a5f]/60 bg-[#071428]/70 p-4 backdrop-blur-md transition-all ${
                  i === 4 ? 'col-span-2' : ''
                }`}
              >
                <div className="mb-3 flex items-center gap-3">
                  <div
                    className="h-10 w-10 flex-shrink-0 overflow-hidden rounded-xl"
                    style={{ background: npc.color }}
                  >
                    <img
                      alt={`${npc.name} NPC avatar`}
                      className="h-full w-full"
                      src={`https://api.dicebear.com/7.x/pixel-art/svg?seed=${npc.seed}`}
                    />
                  </div>
                  <div>
                    <div className="text-sm font-bold text-white">{npc.name}</div>
                    <div className="flex items-center gap-1.5">
                      <div className="h-1.5 w-1.5 rounded-full bg-[#22d3ee]" />
                      <span className="text-[10px] font-bold tracking-wider text-[#22d3ee]">
                        {npc.status}
                      </span>
                    </div>
                  </div>
                </div>
                <p className="text-[11px] italic leading-snug text-sky-500">
                  &quot;{npc.tagline}&quot;
                </p>
                <div className="mt-3 flex gap-2">
                  <button
                    className="flex-1 rounded-lg border border-[#1e3a5f]/70 bg-[#0a1a30]/80 py-1.5 text-[10px] font-bold text-sky-200 transition-colors hover:border-[#0ea5e9]/50"
                    type="button"
                  >
                    Chat
                  </button>
                  <button
                    className="flex-1 rounded-lg border border-[#1e3a5f]/70 bg-[#0a1a30]/80 py-1.5 text-[10px] font-bold text-sky-200 transition-colors hover:border-[#22d3ee]/40"
                    type="button"
                  >
                    Task
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export function LandingPageAgentSystem(): JSX.Element {
  return (
    <section className="py-24" id="agents">
      <div className="mx-auto max-w-7xl px-5">
        <div className="overflow-hidden rounded-3xl border border-[#0ea5e9]/15 backdrop-blur-sm">
          <div className="grid grid-cols-1 items-start gap-14 bg-gradient-to-br from-[#071428]/90 to-[#060f1e]/95 p-10 lg:grid-cols-2 lg:p-16">
            <div>
              <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#06b6d4]/30 bg-[#06b6d4]/12 px-3 py-1.5 text-xs font-bold tracking-wide text-[#67e8f9] backdrop-blur-sm">
                <Cpu className="h-3 w-3" />
                <span>AUTONOMOUS AGENTS</span>
              </div>
              <h2 className="mb-6 text-4xl font-black leading-tight tracking-tight text-white">
                NPCs that use
                <br />
                <span className="text-[#22d3ee]">real tools.</span>
                <br />
                In the real world.
              </h2>
              <p className="mb-10 leading-relaxed text-sky-300/80">
                User-created NPCs aren&apos;t just chatbots. They run full autonomous agent loops
                &mdash; searching the web, generating documents, executing Python code, and
                navigating the world &mdash; all in the background.
              </p>
              <div className="space-y-5">
                {AGENT_TOOLS.map((tool) => (
                  <div key={tool.label} className="flex items-start gap-4">
                    <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border border-[#22d3ee]/25 bg-[#06b6d4]/12">
                      <tool.icon className="h-4 w-4 text-[#22d3ee]" />
                    </div>
                    <div>
                      <div className="mb-0.5 text-sm font-bold text-white">{tool.label}</div>
                      <div className="text-sm text-sky-400/70">{tool.detail}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Terminal mock */}
            <div className="overflow-hidden rounded-2xl border border-[#0ea5e9]/15 bg-[#040810]/90 shadow-2xl shadow-[#0ea5e9]/5 backdrop-blur-md">
              <div className="flex items-center gap-2 border-b border-[#1e3a5f]/60 px-4 py-3">
                <div className="h-2.5 w-2.5 rounded-full bg-[#0ea5e9]/60" />
                <div className="h-2.5 w-2.5 rounded-full bg-[#22d3ee]/60" />
                <div className="h-2.5 w-2.5 rounded-full bg-[#38bdf8]/60" />
                <span className="ml-2 font-mono text-[11px] text-sky-700">
                  agent-runtime &middot; waysprite
                </span>
              </div>
              <div className="space-y-1.5 p-5 font-mono text-xs leading-relaxed">
                <p className="text-[#38bdf8]">
                  $ waysprite agent init --name &quot;Archivist&quot;
                </p>
                <p className="text-sky-700">Initializing agent &apos;Archivist&apos;...</p>
                <p className="text-[#22d3ee]">
                  &#8627; loading skills: web_search, file_gen, spatial_nav
                </p>
                <p className="text-sky-300">
                  Goal: &quot;Summarize today&apos;s tech news, place a scroll in the Library.&quot;
                </p>
                <div className="my-2 border-t border-[#1e3a5f]/50" />
                <p className="text-[#06b6d4]">[1/4] Searching Google News API...</p>
                <p className="text-[#06b6d4]">[2/4] Synthesizing with Claude 3.5 Sonnet...</p>
                <p className="text-[#06b6d4]">[3/4] Generating map object: scroll_v1.png</p>
                <p className="text-[#06b6d4]">[4/4] Placing at coord [38, 21] in Library zone...</p>
                <div className="my-2 border-t border-[#1e3a5f]/50" />
                <p className="text-[#22d3ee]">&#10003; Archivist is now patrolling the Library.</p>
                <p className="text-sky-700">
                  tokens: 1,842 &middot; cost: $0.004 &middot; exec: 3.2s
                </p>
                <div className="mt-1 flex items-center gap-1">
                  <span className="text-[#38bdf8]">$</span>
                  <motion.span
                    animate={{ opacity: [1, 0] }}
                    transition={{ duration: 0.7, repeat: Infinity }}
                    className="inline-block h-3.5 w-1.5 rounded-sm bg-[#0ea5e9]"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function LandingPageLevelEditor(): JSX.Element {
  return (
    <section
      className="border-y border-[#0ea5e9]/10 py-24"
      id="editor"
      style={{ background: 'linear-gradient(180deg, #060f1e 0%, #050b1a 100%)' }}
    >
      <div className="mx-auto max-w-7xl px-5">
        <div className="grid grid-cols-1 items-center gap-16 lg:grid-cols-2">
          <div className="relative">
            <div
              className="grid gap-1 rounded-2xl border border-[#0ea5e9]/12 bg-[#040810]/90 p-4"
              style={{ gridTemplateColumns: 'repeat(12, 1fr)' }}
            >
              {Array.from({ length: 144 }, (_, i) => {
                const row = Math.floor(i / 12);
                const col = i % 12;
                const isGrass = (row + col) % 3 !== 0;
                const isTree = [5, 18, 33, 62, 77, 91, 110].includes(i);
                const isPath = row === 6 || col === 6;
                return (
                  <div
                    key={i}
                    className={`aspect-square rounded-[2px] transition-all ${
                      isTree
                        ? 'bg-[#164e63]'
                        : isPath
                          ? 'bg-[#0ea5e9]/35'
                          : isGrass
                            ? 'bg-[#071e38]'
                            : 'bg-[#050f20]'
                    }`}
                  />
                );
              })}
            </div>
            <motion.div
              initial={{ opacity: 0, scale: 0.92 }}
              whileInView={{ opacity: 1, scale: 1 }}
              className="absolute inset-0 flex items-center justify-center"
            >
              <div className="w-72 rounded-2xl border border-[#0ea5e9]/25 bg-[#071428]/92 p-6 shadow-2xl shadow-[#0ea5e9]/10 backdrop-blur-xl">
                <div className="mb-4 flex items-center justify-between">
                  <span className="text-sm font-bold text-white">AI Layout Generator</span>
                  <Wand2 className="h-4 w-4 text-[#22d3ee]" />
                </div>
                <div className="mb-4 rounded-xl border border-[#1e3a5f]/60 bg-[#040810]/80 p-3 text-[11px] italic leading-relaxed text-sky-400/80">
                  &quot;Create a cozy forest clearing with a stone fountain in the center and a
                  wooden bench to the north.&quot;
                </div>
                <div className="mb-3 flex gap-2">
                  {LAYER_LABELS.map((layer) => (
                    <span
                      key={layer.label}
                      className="rounded-lg border border-[#22d3ee]/20 bg-[#06b6d4]/12 px-2 py-1 text-[9px] font-bold text-[#38bdf8]"
                    >
                      {layer.label}
                    </span>
                  ))}
                </div>
                <button
                  className="w-full rounded-xl bg-gradient-to-r from-[#0ea5e9] to-[#22d3ee] py-2.5 text-sm font-black text-[#050b1a] transition-colors hover:from-[#38bdf8] hover:to-[#67e8f9]"
                  type="button"
                >
                  Generate Tiles
                </button>
              </div>
            </motion.div>
          </div>
          <div>
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#0ea5e9]/30 bg-[#0ea5e9]/12 px-3 py-1.5 text-xs font-bold tracking-wide text-[#38bdf8] backdrop-blur-sm">
              <Wand2 className="h-3 w-3" />
              <span>LEVEL EDITOR</span>
            </div>
            <h2 className="mb-6 text-4xl font-black leading-tight tracking-tight text-white">
              Build worlds with
              <br />
              <span className="text-[#22d3ee]">AI-assisted</span>
              <br />
              tile generation
            </h2>
            <p className="mb-8 text-lg leading-relaxed text-sky-300/80">
              The 12&times;12 tile editor pairs a pro-grade layer system with a Gemini pipeline that
              turns plain-English descriptions into fully-formed game levels in seconds.
            </p>
            <ul className="space-y-3">
              {EDITOR_BULLETS.map((b) => (
                <li key={b.label} className="flex items-start gap-3 text-sm text-sky-300/80">
                  <div className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#0ea5e9]" />
                  <span>{b.label}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
